import {
  assignPrototypeAPIs,
  assignTableAPIs,
  functionalUpdate,
  type Row,
  type RowData,
  type Table,
  type TableFeature,
  type Updater,
} from '@tanstack/react-table';

import type { TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';
import type { DataTableRowOrderState } from './row-order-types.js';

/**
 * Helper function to update the row order state on the table, which can be called from row-level actions to ensure consistent state management.
 * @internal
 */
export function table_setDraggingRowInfo<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  draggingRowInfo: {
    id: string;
    index: number;
    row: Row<TDataTableFeatures, RowData>;
  } | null,
) {
  table.baseAtoms.draggingRowInfo.set(draggingRowInfo);
}

/**
 * Helper function to update the row order state on the table, which can be called from row-level actions to ensure consistent state management.
 * @internal
 */
export function table_setRowOrder<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  updater: Updater<
    DataTableRowOrderState<TDataTableFeatures, TData>['rowOrder']
  >,
) {
  const newValue = functionalUpdate(updater, table.atoms.rowOrder?.get() ?? []);

  queueMicrotask(() => {
    table.options.onRowOrderChange?.(newValue);
  });
}

/**
 * Helper function to access the row model before the row order is applied on the table, which can be called from row-level actions to ensure consistent state management.
 * @internal
 */
export function table_getPreRowOrderRowModel<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
) {
  return table.getExpandedRowModel();
}

/**
 * Helper function to access the row order model on the table, which can be called from row-level actions to ensure consistent state management.
 * @internal
 */
export function table_getRowOrderRowModel<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
) {
  table._rowModels.rowOrderRowModel ??= table.options.rowOrderRowModel?.(table);
  if (!table.options.enableRowOrdering || !table._rowModels.rowOrderRowModel) {
    return table.getPreRowOrderRowModel();
  }
  return table._rowModels.rowOrderRowModel();
}

/**
 * Helper function to determine if a specific row can be dragged, based on the table options and the row's properties.
 * @internal
 */
export function row_getCanDrag<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
): boolean {
  const { enableRowOrdering, enableRowOrderDragAndDrop } = row.table.options;
  const sortingState = row.table.atoms.sorting?.get();

  if (
    enableRowOrdering !== true ||
    // always disable the drag and drop handle if the rows are sorted
    (sortingState !== undefined && sortingState.length > 0)
  ) {
    return false;
  }

  // check if drag and drop is disabled for the row
  if (
    enableRowOrderDragAndDrop !== undefined &&
    typeof enableRowOrderDragAndDrop === 'function'
  ) {
    return enableRowOrderDragAndDrop(row);
  }
  return true;
}

/**
 * Helper function to determine if a row with disabled drag and drop should be locked in place based on the table options and the row's position in the table.
 * @internal
 */
export function row_getDisabledRowDragAndDropLockPosition<
  TData extends RowData,
>(row: Row<TDataTableFeatures, TData>): boolean | 'start' | 'end' | undefined {
  return row.table.options.lockDisabledRows ?? false;
}

/**
 * Constructs the column row ordering for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowOrderFeature<
  TData extends RowData,
>(): TableFeature {
  return {
    getInitialState: (initialState) => {
      return {
        rowOrder: structuredClone([]),
        draggingRowInfo: null,
        ...initialState,
      };
    },
    assignRowPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('DataTableRowOrderFeature', prototype, table, {
          row_getCanDrag: { fn: (row) => row_getCanDrag(row) },
          row_getDisabledRowDragAndDropLockPosition: {
            fn: (row) => row_getDisabledRowDragAndDropLockPosition(row),
          },
        });
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('DataTableRowOrderFeature', table, {
          table_setDraggingRowInfo: {
            fn: (draggingRowInfo) =>
              table_setDraggingRowInfo(table, draggingRowInfo),
          },
          table_setRowOrder: {
            fn: (
              updater: Updater<
                DataTableRowOrderState<TDataTableFeatures, TData>['rowOrder']
              >,
            ) => table_setRowOrder(table, updater),
          },
          table_getPreRowOrderRowModel: {
            fn: () => table_getPreRowOrderRowModel(table),
          },
          table_getRowOrderRowModel: {
            fn: () => table_getRowOrderRowModel(table),
          },
        });
      },
    ),
  };
}

/**
 * Feature implementation for Row Order.
 * @internal
 */
export const DataTableRowOrderFeature = constructRowOrderFeature();
